import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { authApi, dashboardApi, tradesApi, adminApi, signalsApi, notificationsApi, commentsApi, subscriptionsApi, brokerageApi } from '../api/index';
import type { AffiliatePayoutDetailsInput } from '../api/index';
import { User, DashboardData, Trade, MasterTrader, MasterPlatformFilter, CopyRelationship, CopySettings, Signal, Notification, Comment, Subscription, Brokerage, BrokerageConnection } from '../types';

// Custom hook for API calls with React Query
export const useApi = {
  // Auth hooks
  useCurrentUser: (options?: Omit<UseQueryOptions<{ success: boolean; user: User }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['auth', 'me'],
      queryFn: () => authApi.getCurrentUser(),
      ...options,
    });
  },

  useLogin: (options?: UseMutationOptions<{ success: boolean; token: string; user: User }, Error, { email: string; password: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => authApi.login(data),
      onSuccess: (data) => {
        if (data.token) {
          localStorage.setItem('token', data.token);
          queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        }
      },
      ...options,
    });
  },

  useUpdateProfile: (options?: UseMutationOptions<{ success: boolean; user: User }, Error, any>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => authApi.updateProfile(data),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['user'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        if (options?.onSuccess) (options.onSuccess as any)(data, variables, onMutateResult, context);
      },
      ...options,
    });
  },

  useAcceptRiskAgreement: (options?: UseMutationOptions<{ success: boolean; user: User }, Error, void>) => {
    const queryClient = useQueryClient();
    return useMutation({
      ...options,
      mutationFn: () => authApi.acceptRiskAgreement(),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        (options?.onSuccess as any)?.(data, variables, onMutateResult, context);
      },
    });
  },

  useAcceptMasterAgreement: (options?: UseMutationOptions<{ success: boolean; user: User }, Error, void>) => {
    const queryClient = useQueryClient();
    return useMutation({
      ...options,
      mutationFn: () => authApi.acceptMasterAgreement(),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        (options?.onSuccess as any)?.(data, variables, onMutateResult, context);
      },
    });
  },

  useUpdateOptionRm: (options?: UseMutationOptions<{ success: boolean; user: User }, Error, number>) => {
    const queryClient = useQueryClient();
    return useMutation({
      ...options,
      mutationFn: (optionRm: number) => authApi.updateOptionRm(optionRm),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        (options?.onSuccess as any)?.(data, variables, onMutateResult, context);
      },
    });
  },

  useUpdateGenesisRm: (options?: UseMutationOptions<{ success: boolean; user: User }, Error, number>) => {
    const queryClient = useQueryClient();
    return useMutation({
      ...options,
      mutationFn: (genesisRm: number) => authApi.updateGenesisRm(genesisRm),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        (options?.onSuccess as any)?.(data, variables, onMutateResult, context);
      },
    });
  },

  useRegister: (options?: UseMutationOptions<{ success: boolean; token: string; user: User }, Error, any>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => authApi.register(data),
      onSuccess: (data) => {
        if (data.token) {
          localStorage.setItem('token', data.token);
          queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        }
      },
      ...options,
    });
  },

  useForgotPassword: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    return useMutation({
      mutationFn: (email) => authApi.forgotPassword(email),
      ...options,
    });
  },

  useResetPassword: (options?: UseMutationOptions<{ success: boolean; message?: string; token?: string; user?: User }, Error, { email?: string; otp?: string; password: string; resetToken?: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => authApi.resetPassword(data),
      onSuccess: (data) => {
        if (data.token) {
          localStorage.setItem('token', data.token);
          if (data.user) {
            queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
          }
        }
      },
      ...options,
    });
  },

  useSaveOandaToken: (options?: UseMutationOptions<{ success: boolean; message?: string; user: User }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (accessToken) => authApi.saveOandaToken(accessToken),
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        if (options?.onSuccess) (options.onSuccess as any)(data, variables, onMutateResult, context);
      },
      ...options,
    });
  },

  // Dashboard hooks
  useDashboard: (brokerId?: string | null, options?: UseQueryOptions<DashboardData, Error>) => {
    const currentBrokerId = brokerId || 'primary';
    return useQuery({
      queryKey: ['dashboard', currentBrokerId],
      queryFn: () => dashboardApi.getDashboard(brokerId),
      staleTime: 2 * 60 * 1000, // OPTIMIZATION: Cache for 2 minutes for instant loads
      gcTime: 10 * 60 * 1000, // Cache for 10 minutes
      ...options,
    });
  },

  useMasters: (brokerId?: string | null, options?: Omit<UseQueryOptions<{ success: boolean; masters: MasterTrader[]; platformFilters?: MasterPlatformFilter[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['dashboard', 'masters', brokerId || 'primary'],
      queryFn: () => dashboardApi.getMasters(brokerId),
      enabled: false, // Only fetch when explicitly enabled (for followers only)
      staleTime: 0, // follow/unfollow should reflect server state immediately after refetch
      ...options,
    });
  },

  useTopTraderLeaderboard: (
    brokerType?: 'all' | 'webull' | 'genesis' | 'forex',
    options?: Omit<
      UseQueryOptions<
        {
          success: boolean;
          rows: Array<{
            rank: number;
            masterId: string;
            name: string;
            avatar: string;
            market?: string;
            pnl: number;
            roi: number;
            winRate: number;
            trades: number;
            followers: number;
            status: string;
          }>;
          summary: {
            totalMasters: number;
            totalFollowers: number;
            avgRoi: number;
            activeTraders: number;
          };
        },
        Error
      >,
      'queryKey' | 'queryFn'
    >
  ) => {
    return useQuery({
      queryKey: ['dashboard', 'top-traders-leaderboard', brokerType || 'all'],
      queryFn: () =>
        dashboardApi.getTopTraderLeaderboard({
          brokerType: brokerType || 'all',
          limit: 100,
        }),
      staleTime: 60 * 1000,
      ...options,
    });
  },

  useAffiliateReferrals: (options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof dashboardApi.getAffiliateReferrals>>, Error>,
    'queryKey' | 'queryFn'
  >) => {
    return useQuery({
      queryKey: ['dashboard', 'affiliate', 'referrals'],
      queryFn: () => dashboardApi.getAffiliateReferrals(),
      staleTime: 30 * 1000,
      ...options,
    });
  },

  useAffiliatePayoutDetails: (options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof dashboardApi.getAffiliatePayoutDetails>>, Error>,
    'queryKey' | 'queryFn'
  >) => {
    return useQuery({
      queryKey: ['dashboard', 'affiliate', 'payout-details'],
      queryFn: () => dashboardApi.getAffiliatePayoutDetails(),
      ...options,
    });
  },

  useSaveAffiliatePayoutDetails: (options?: UseMutationOptions<
    Awaited<ReturnType<typeof dashboardApi.saveAffiliatePayoutDetails>>,
    Error,
    AffiliatePayoutDetailsInput
  >) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => dashboardApi.saveAffiliatePayoutDetails(data),
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'affiliate', 'payout-details'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useAccountAllocation: (options?: Omit<UseQueryOptions<{ success: boolean; accounts: any[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['copyTrading', 'accountAllocation'],
      queryFn: () => dashboardApi.getAccountAllocation(),
      staleTime: 0,
      refetchOnMount: 'always',
      ...options,
    });
  },

  useEnableCopyTrading: (options?: UseMutationOptions<{ success: boolean; message?: string; copyRelationship?: any }, Error, CopySettings & { followerBrokerAccountId?: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => dashboardApi.enableCopyTrading(data),
      ...options,
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: ['dashboard', 'masters'] });
        const previous = queryClient.getQueriesData({ queryKey: ['dashboard', 'masters'] });
        const mid = variables.masterUserId;
        queryClient.setQueriesData({ queryKey: ['dashboard', 'masters'] }, (old: any) => {
          if (!old?.masters) return old;
          return {
            ...old,
            masters: old.masters.map((m: any) =>
              String(m._id) === String(mid) ? { ...m, isFollowing: true, relationshipStatus: 'active' } : m
            ),
          };
        });
        return { previous };
      },
      onError: (err, variables, onMutateResult, context) => {
        (onMutateResult as { previous?: [unknown, unknown][] } | undefined)?.previous?.forEach(([key, data]) => {
          queryClient.setQueryData(key as any, data);
        });
        options?.onError?.(err, variables, onMutateResult, context);
      },
      // onSuccess defined AFTER ...options so it always runs even when caller passes onSuccess
      onSuccess: async (data, variables, onMutateResult, context) => {
        const mid = variables.masterUserId;
        queryClient.setQueriesData({ queryKey: ['dashboard', 'masters'] }, (old: any) => {
          if (!old?.masters) return old;
          return {
            ...old,
            masters: old.masters.map((m: any) =>
              String(m._id) === String(mid)
                ? {
                    ...m,
                    isFollowing: true,
                    relationshipStatus: 'active',
                    relationshipId:
                      data?.copyRelationship?.id ||
                      data?.copyRelationship?._id ||
                      m.relationshipId,
                  }
                : m
            ),
          };
        });
        await queryClient.refetchQueries({ queryKey: ['dashboard', 'masters'] });
        await queryClient.refetchQueries({ queryKey: ['copyTrading', 'accountAllocation'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['masterDetails'] });
        queryClient.invalidateQueries({ queryKey: ['trades'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useMasterBrokerAccounts: (masterId: string | null, options?: Omit<UseQueryOptions<{ success: boolean; brokerAccounts: any[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['dashboard', 'masters', masterId, 'broker-accounts'],
      queryFn: () => dashboardApi.getMasterBrokerAccounts(masterId!),
      enabled: !!masterId,
      ...options,
    });
  },

  useEnableCopyTradingBrokerAccounts: (options?: UseMutationOptions<{ success: boolean; message?: string; relationships?: any[] }, Error, { masterUserId: string; masterBrokerAccountIds: string[]; allocationPercent: number; maxInvestmentAmount?: number; endDate?: string; followerBrokerAccountId?: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => dashboardApi.enableCopyTradingBrokerAccounts(data),
      ...options,
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: ['dashboard', 'masters'] });
        const previous = queryClient.getQueriesData({ queryKey: ['dashboard', 'masters'] });
        const mid = variables.masterUserId;
        queryClient.setQueriesData({ queryKey: ['dashboard', 'masters'] }, (old: any) => {
          if (!old?.masters) return old;
          return {
            ...old,
            masters: old.masters.map((m: any) =>
              String(m._id) === String(mid) ? { ...m, isFollowing: true, relationshipStatus: 'active' } : m
            ),
          };
        });
        return { previous };
      },
      onError: (err, variables, onMutateResult, context) => {
        (onMutateResult as { previous?: [unknown, unknown][] } | undefined)?.previous?.forEach(([key, data]) => {
          queryClient.setQueryData(key as any, data);
        });
        options?.onError?.(err, variables, onMutateResult, context);
      },
      onSuccess: async (data, variables, onMutateResult, context) => {
        const mid = variables.masterUserId;
        const relationshipId = data?.relationships?.[0]?.id || undefined;
        queryClient.setQueriesData({ queryKey: ['dashboard', 'masters'] }, (old: any) => {
          if (!old?.masters) return old;
          return {
            ...old,
            masters: old.masters.map((m: any) =>
              String(m._id) === String(mid)
                ? {
                    ...m,
                    isFollowing: true,
                    relationshipStatus: 'active',
                    relationshipId: relationshipId || m.relationshipId,
                  }
                : m
            ),
          };
        });
        await queryClient.refetchQueries({ queryKey: ['dashboard', 'masters'] });
        await queryClient.refetchQueries({ queryKey: ['copyTrading', 'accountAllocation'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['masterDetails'] });
        queryClient.invalidateQueries({ queryKey: ['trades'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useCancelCopyTrading: (options?: UseMutationOptions<{ success: boolean; message?: string; closedPositions?: number }, Error, { relationshipId: string; closePositions: boolean }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ relationshipId, closePositions }) => dashboardApi.cancelCopyTrading(relationshipId, closePositions),
      ...options,
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: ['dashboard', 'masters'] });
        const previous = queryClient.getQueriesData({ queryKey: ['dashboard', 'masters'] });
        const rid = variables.relationshipId;
        queryClient.setQueriesData({ queryKey: ['dashboard', 'masters'] }, (old: any) => {
          if (!old?.masters) return old;
          return {
            ...old,
            masters: old.masters.map((m: any) =>
              m.relationshipId === rid
                ? { ...m, isFollowing: false, relationshipId: undefined, relationshipStatus: undefined }
                : m
            ),
          };
        });
        return { previous };
      },
      onError: (err, variables, onMutateResult, context) => {
        (onMutateResult as { previous?: [unknown, unknown][] } | undefined)?.previous?.forEach(([key, data]) => {
          queryClient.setQueryData(key as any, data);
        });
        options?.onError?.(err, variables, onMutateResult, context);
      },
      onSuccess: async (data, variables, onMutateResult, context) => {
        await queryClient.refetchQueries({ queryKey: ['dashboard', 'masters'] });
        await queryClient.refetchQueries({ queryKey: ['copyTrading', 'accountAllocation'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['trades'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  usePauseCopyTradingForDay: (
    options?: UseMutationOptions<
      {
        success: boolean;
        message?: string;
        alreadyPaused?: boolean;
        pausedCount?: number;
        copyTradingResumeAt?: string;
      },
      Error,
      void
    >
  ) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () => dashboardApi.pauseCopyTradingForDay(),
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useUpdateCopySettings: (options?: UseMutationOptions<{ success: boolean; message?: string; relationship?: CopyRelationship }, Error, { relationshipId: string; allocationPercent?: number; maxInvestmentAmount?: number; endDate?: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => dashboardApi.updateCopySettings(data),
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['copyTrading', 'accountAllocation'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useCopyRelationships: (brokerId?: string | null, options?: Omit<UseQueryOptions<{ success: boolean; relationships: CopyRelationship[] }, Error>, 'queryKey' | 'queryFn'> & { allBrokers?: boolean }) => {
    const { allBrokers, ...queryOptions } = options || {};
    return useQuery({
      queryKey: ['copyRelationships', allBrokers ? 'all' : (brokerId || 'primary')],
      queryFn: () => dashboardApi.getCopyRelationships(brokerId, { allBrokers }),
      staleTime: 0, // Always refetch when invalidated (no stale time)
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnWindowFocus: true, // Refetch when window regains focus
      ...queryOptions,
    });
  },

  useCopyPerformance: (relationshipId: string, options?: Omit<UseQueryOptions<{
    success: boolean;
    relationship: CopyRelationship;
    trades: Trade[];
    performance: any;
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['copyPerformance', relationshipId],
      queryFn: () => dashboardApi.getCopyPerformance(relationshipId),
      enabled: !!relationshipId,
      ...options,
    });
  },

  useTrades: (params?: { page?: number; limit?: number; status?: string; type?: string; brokerId?: string | null; days?: number; }, options?: Omit<UseQueryOptions<{
    success: boolean;
    trades: Trade[];
    totalPages: number;
    currentPage: number;
    total: number
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['trades', params],
      queryFn: () => tradesApi.getTrades(params),
      staleTime: 30 * 1000, 
      gcTime: 5 * 60 * 1000, 
      ...options,
    });
  },

  // Admin hooks
  usePendingMasters: (options?: Omit<UseQueryOptions<{ success: boolean; users: User[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['admin', 'pending-masters'],
      queryFn: () => adminApi.getPendingMasters(),
      enabled: false, // Only fetch when explicitly enabled
      ...options,
    });
  },

  useApproveMaster: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, { userId: string; tradingType: 'swing' | 'day' }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ userId, tradingType }) => adminApi.approveMaster(userId, tradingType),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'pending-masters'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      },
      ...options,
    });
  },

  useRejectMaster: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (userId) => adminApi.rejectMaster(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'pending-masters'] });
      },
      ...options,
    });
  },

  usePendingFollowers: (options?: Omit<UseQueryOptions<{ success: boolean; users: User[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['admin', 'pending-followers'],
      queryFn: () => adminApi.getPendingFollowers(),
      enabled: false, // Only fetch when explicitly enabled
      ...options,
    });
  },

  useApproveFollower: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (userId) => adminApi.approveFollower(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'pending-followers'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      },
      ...options,
    });
  },

  useRejectFollower: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (userId) => adminApi.rejectFollower(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'pending-followers'] });
      },
      ...options,
    });
  },

  useDeleteUser: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (userId) => adminApi.deleteUser(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] }); // Invalidate stats when user is deleted
      },
      ...options,
    });
  },

  // Signals hooks
  useDeleteSignal: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (signalId: string) => signalsApi.deleteSignal(signalId),
      onSuccess: (data, signalId) => {
        // Optimistically remove signal from all cached queries
        const queryCache = queryClient.getQueryCache();
        const allSignalQueries = queryCache.findAll({ queryKey: ['signals'] });

        allSignalQueries.forEach((query) => {
          const oldData = query.state.data as any;
          if (oldData && oldData.signals) {
            const updatedSignals = oldData.signals.filter((s: any) => s._id !== signalId);
            queryClient.setQueryData(query.queryKey, {
              ...oldData,
              signals: updatedSignals,
              pagination: {
                ...oldData.pagination,
                total: Math.max(0, (oldData.pagination?.total || 0) - 1)
              }
            });
          }
        });

        // Also update master signals queries
        const masterQueries = queryCache.findAll({ queryKey: ['signals', 'master'] });
        masterQueries.forEach((query) => {
          const oldData = query.state.data as any;
          if (oldData && oldData.signals) {
            const updatedSignals = oldData.signals.filter((s: any) => s._id !== signalId);
            queryClient.setQueryData(query.queryKey, {
              ...oldData,
              signals: updatedSignals,
              pagination: {
                ...oldData.pagination,
                total: Math.max(0, (oldData.pagination?.total || 0) - 1)
              }
            });
          }
        });

        // Invalidate to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['signals'] });
      },
      ...options,
    });
  },
  useSignals: (page?: number, limit?: number, type?: 'signal' | 'post', options?: Omit<UseQueryOptions<{
    success: boolean;
    signals: Signal[];
    pagination: any;
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['signals', page, limit, type],
      queryFn: () => signalsApi.getSignals(page, limit, type),
      staleTime: 3 * 60 * 1000, // Data stays fresh for 3 minutes
      gcTime: 10 * 60 * 1000, // Cache for 10 minutes
      ...options,
    });
  },

  useMasterSignals: (masterId: string, page?: number, limit?: number, type?: 'signal' | 'post', options?: Omit<UseQueryOptions<{
    success: boolean;
    signals: Signal[];
    pagination: any;
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['signals', 'master', masterId, page, limit, type],
      queryFn: () => signalsApi.getMasterSignals(masterId, page, limit, type),
      enabled: !!masterId,
      staleTime: 3 * 60 * 1000, // Data stays fresh for 3 minutes
      gcTime: 10 * 60 * 1000, // Cache for 10 minutes
      ...options,
    });
  },

  useCreateSignal: (options?: UseMutationOptions<{ success: boolean; signal: Signal }, Error, {
    type: 'signal' | 'post';
    symbol?: string;
    price?: number;
    side?: 'buy' | 'sell';
    target?: number;
    stopLoss?: number;
    description: string;
  }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => signalsApi.createSignal(data),
      onSuccess: (data) => {
        if (data.signal) {
          const signalType = data.signal.type;

          // Get all cached signal queries and update them
          const queryCache = queryClient.getQueryCache();
          const allSignalQueries = queryCache.findAll({ queryKey: ['signals'] });

          allSignalQueries.forEach((query) => {
            const queryKey = query.queryKey;
            const typeParam = queryKey[3]; // Get the type filter from query key

            // Update if it's "all" filter (undefined) or matches the signal type
            if (typeParam === undefined || typeParam === signalType) {
              const oldData = query.state.data as any;
              if (oldData && oldData.signals) {
                queryClient.setQueryData(queryKey, {
                  ...oldData,
                  signals: [data.signal, ...oldData.signals],
                  pagination: {
                    ...oldData.pagination,
                    total: (oldData.pagination?.total || 0) + 1
                  }
                });
              }
            }
          });

          // Update master signals queries
          const masterQueries = queryCache.findAll({ queryKey: ['signals', 'master'] });
          masterQueries.forEach((query) => {
            const oldData = query.state.data as any;
            if (oldData && oldData.signals) {
              queryClient.setQueryData(query.queryKey, {
                ...oldData,
                signals: [data.signal, ...oldData.signals],
                pagination: {
                  ...oldData.pagination,
                  total: (oldData.pagination?.total || 0) + 1
                }
              });
            }
          });
        }

        // Invalidate all signal queries to ensure consistency and refetch
        queryClient.invalidateQueries({ queryKey: ['signals'] });
        queryClient.invalidateQueries({ queryKey: ['signals', 'master'] });
      },
      ...options,
    });
  },

  useLikeSignal: (options?: UseMutationOptions<{ success: boolean; isLiked: boolean; likesCount: number; likes?: string[] }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (signalId) => signalsApi.likeSignal(signalId),
      onSuccess: (data, signalId) => {
        console.log('🔄 useLikeSignal onSuccess:', { signalId, data });

        // Update all signal queries with the actual likes array from API response
        queryClient.setQueriesData({ queryKey: ['signals'] }, (old: any) => {
          if (!old || !old.signals) return old;
          return {
            ...old,
            signals: old.signals.map((signal: Signal) => {
              if (signal._id === signalId) {
                // Use the likes array from API response if provided
                const updatedSignal = {
                  ...signal,
                  likesCount: data.likesCount,
                  likes: data.likes || signal.likes // Use API response likes array, fallback to existing
                };
                console.log('✅ Updated signal in cache:', {
                  signalId,
                  oldLikes: signal.likes,
                  newLikes: updatedSignal.likes,
                  isLiked: data.isLiked
                });
                return updatedSignal;
              }
              return signal;
            })
          };
        });

        // Also update master signals queries
        queryClient.setQueriesData({ queryKey: ['signals', 'master'] }, (old: any) => {
          if (!old || !old.signals) return old;
          return {
            ...old,
            signals: old.signals.map((signal: Signal) => {
              if (signal._id === signalId) {
                return {
                  ...signal,
                  likesCount: data.likesCount,
                  likes: data.likes || signal.likes
                };
              }
              return signal;
            })
          };
        });

        // Invalidate to ensure we get fresh data with correct likes array
        queryClient.invalidateQueries({ queryKey: ['signals'] });
      },
      ...options,
    });
  },

  // Notifications hooks
  useNotifications: (page?: number, limit?: number, filter?: 'all' | 'unread' | 'read', options?: Omit<UseQueryOptions<{
    success: boolean;
    notifications: Notification[];
    unreadCount: number;
    pagination: any;
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['notifications', page, limit, filter],
      queryFn: () => notificationsApi.getNotifications(page, limit, filter),
      staleTime: 1 * 60 * 1000, // Data stays fresh for 1 minute (shorter for notifications)
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      ...options,
    });
  },

  useUnreadCount: (options?: Omit<UseQueryOptions<{ success: boolean; unreadCount: number }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['notifications', 'unread-count'],
      queryFn: () => notificationsApi.getUnreadCount(),
      staleTime: 1 * 60 * 1000, // Data stays fresh for 1 minute
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      ...options,
    });
  },

  useMarkAsRead: (options?: UseMutationOptions<{ success: boolean; notification: Notification }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (notificationId) => notificationsApi.markAsRead(notificationId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      },
      ...options,
    });
  },

  useMarkAllAsRead: (options?: UseMutationOptions<{ success: boolean; updatedCount: number }, Error>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () => notificationsApi.markAllAsRead(),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      },
      ...options,
    });
  },

  // Comments hooks
  useComments: (signalId: string, options?: Omit<UseQueryOptions<{ success: boolean; comments: Comment[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['comments', signalId],
      queryFn: () => commentsApi.getComments(signalId),
      enabled: !!signalId,
      staleTime: 30 * 1000, // Data stays fresh for 30 seconds
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchOnMount: 'always',
      ...options,
    });
  },

  useCreateComment: (options?: UseMutationOptions<{ success: boolean; comment: Comment }, Error, { signalId: string; text: string; parentCommentId?: string }>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data) => commentsApi.createComment(data),
      onSuccess: (data, variables) => {
        // Optimistically update the comments list
        queryClient.setQueryData<{ success: boolean; comments: Comment[] }>(
          ['comments', variables.signalId],
          (old) => {
            if (!old) return { success: true, comments: [data.comment] };
            return {
              ...old,
              comments: [...old.comments, data.comment],
            };
          }
        );
        // Invalidate to refetch and ensure consistency
        queryClient.invalidateQueries({ queryKey: ['comments', variables.signalId] });
      },
      ...options,
    });
  },

  useLikeComment: (options?: UseMutationOptions<{ success: boolean; isLiked: boolean; likesCount: number; likes: string[] }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (commentId) => commentsApi.likeComment(commentId),
      onSuccess: (data, commentId) => {
        // Update all comment queries optimistically
        queryClient.setQueriesData({ queryKey: ['comments'] }, (old: any) => {
          if (!old || !old.comments) return old;
          return {
            ...old,
            comments: old.comments.map((comment: Comment) => {
              if (comment._id === commentId) {
                return {
                  ...comment,
                  likesCount: data.likesCount,
                  likes: data.likes,
                };
              }
              return comment;
            }),
          };
        });
        // Also invalidate to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['comments'] });
      },
      ...options,
    });
  },

  useDeleteComment: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (commentId) => commentsApi.deleteComment(commentId),
      onSuccess: (data, commentId) => {
        // Optimistically remove comment from all queries
        queryClient.setQueriesData({ queryKey: ['comments'] }, (old: any) => {
          if (!old || !old.comments) return old;
          return {
            ...old,
            comments: old.comments.filter((comment: Comment) => comment._id !== commentId),
          };
        });
        // Also invalidate to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['comments'] });
      },
      ...options,
    });
  },

  // Subscriptions hooks
  useSubscriptionStatus: (options?: Omit<UseQueryOptions<{
    success: boolean;
    hasSubscription: boolean;
    isActive: boolean;
    canAccessPremiumFeatures?: boolean;
    cancelAtPeriodEnd?: boolean;
    trialEligible?: boolean;
    trialEligibilityReason?: string | null;
    growthEligible?: boolean;
    growthEligibilityReason?: string | null;
    subscription: Subscription | null;
    masterFeeSubscription?: Subscription | null;
  }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['subscription', 'status'],
      queryFn: () => subscriptionsApi.getSubscriptionStatus(),
      staleTime: 15 * 1000, // Refresh often so Whop webhook updates show quickly
      refetchOnWindowFocus: true,
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      ...options,
    });
  },

  useCreateCheckoutSession: (options?: UseMutationOptions<
    { success: boolean; checkoutUrl: string; sessionId: string },
    Error,
    | 'growth'
    | 'general'
    | 'pro'
    | 'custom'
    | 'masterfee'
    | 'masterfollowingfee'
    | 'og'
    | 'special_users'
    | 'old_user'
    | 'full_access_paid'
    | {
        plan?:
          | 'growth'
          | 'general'
          | 'pro'
          | 'custom'
          | 'masterfee'
          | 'masterfollowingfee'
          | 'og'
          | 'special_users'
          | 'old_user'
          | 'full_access_paid';
        billingMode?: 'trial' | 'monthly_instant' | 'onetime_next_month';
        masterId?: string;
      }
    | undefined
  >) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (plan) => subscriptionsApi.createCheckoutSession(plan),
      onSuccess: (data) => {
        const checkoutUrl = data?.checkoutUrl || (data as { url?: string })?.url;
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
      },
      ...options,
    });
  },

  useActivatePending: (options?: UseMutationOptions<{ success: boolean; message?: string; subscription?: Subscription }, Error>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () => subscriptionsApi.activatePending(),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      },
      ...options,
    });

  },

  useCancelSubscription: (options?: UseMutationOptions<{ success: boolean; message?: string; accessUntil?: string }, Error, 'platform' | 'masterfee' | void>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (type) => subscriptionsApi.cancelSubscription(type || undefined),
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  useResumeSubscription: (options?: UseMutationOptions<{ success: boolean; message?: string }, Error, 'platform' | 'masterfee' | void>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (type) => subscriptionsApi.resumeSubscription(type || undefined),
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  },

  // Brokerage hooks
  useSupportedBrokerages: (region?: string, options?: Omit<UseQueryOptions<{ success: boolean; brokerages: Brokerage[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'supported', region],
      queryFn: () => brokerageApi.getSupportedBrokerages(region),
      staleTime: 10 * 60 * 1000, // Data stays fresh for 10 minutes
      gcTime: 30 * 60 * 1000, // Cache for 30 minutes
      ...options,
    });
  },

  useBrokerageConnections: (options?: Omit<UseQueryOptions<{ success: boolean; connections: BrokerageConnection[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'connections'],
      queryFn: () => brokerageApi.getConnections(),
      staleTime: 1 * 60 * 1000, // Data stays fresh for 1 minute
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      ...options,
    });
  },

  useConnectAlpaca: (
    options?: UseMutationOptions<
      {
        success: boolean;
        message?: string;
        connection?: BrokerageConnection;
        account?: { id: string; equity: number; buyingPower: number; accountType: string };
        user?: User;
      },
      Error,
      { apiKey: string; apiSecret: string; isPaper: boolean }
    >,
  ) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ apiKey, apiSecret, isPaper }) =>
        brokerageApi.connectAlpaca(apiKey, apiSecret, isPaper),
      onSuccess: (data) => {
        if (data.user) {
          queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        }
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      },
      ...options,
    });
  },

  useInitSnaptradeConnection: (options?: UseMutationOptions<{ success: boolean; redirectUrl: string; brokerage: { id: string; name: string } }, Error, string>) => {
    return useMutation({
      mutationFn: (brokerageId) => brokerageApi.initSnaptradeConnection(brokerageId),
      ...options,
    });
  },

  useCompleteSnaptradeConnection: (options?: UseMutationOptions<{ success: boolean; message?: string; connection?: BrokerageConnection }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (brokerageId) => brokerageApi.completeSnaptradeConnection(brokerageId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      },
      ...options,
    });
  },

  useSetPrimaryConnection: (options?: UseMutationOptions<{ success: boolean; message?: string; connection?: BrokerageConnection }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (connectionId) => brokerageApi.setPrimaryConnection(connectionId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      },
      ...options,
    });
  },

  useSyncConnection: (options?: UseMutationOptions<{ success: boolean; message?: string; account?: any; connection?: BrokerageConnection }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (connectionId) => brokerageApi.syncConnection(connectionId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      },
      ...options,
    });
  },

  useDisconnectBrokerage: (options?: UseMutationOptions<{ success: boolean; message?: string; unfollowedCount?: number }, Error, string>) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (connectionId) => brokerageApi.disconnectConnection(connectionId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brokerages', 'connections'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
        queryClient.invalidateQueries({ queryKey: ['copyRelationships'] });
        queryClient.invalidateQueries({ queryKey: ['copyTrading'] });
      },
      ...options,
    });
  },

  useBrokeragePositions: (connectionId?: string, options?: Omit<UseQueryOptions<{ success: boolean; positions: any[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'positions', connectionId],
      queryFn: () => brokerageApi.getPositions(connectionId),
      staleTime: 30 * 1000, // Data stays fresh for 30 seconds
      gcTime: 2 * 60 * 1000, // Cache for 2 minutes
      ...options,
    });
  },

  useBrokerageOrders: (connectionId?: string, status?: string, limit?: number, page?: number, options?: Omit<UseQueryOptions<{ success: boolean; orders: any[]; pagination?: any }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'orders', connectionId, status, limit, page],
      queryFn: () => brokerageApi.getOrders(connectionId, status, limit, page),
      staleTime: 30 * 1000, // Data stays fresh for 30 seconds
      gcTime: 2 * 60 * 1000, // Cache for 2 minutes
      ...options,
    });
  },

  useDismissAlert: () => {
    return useMutation({
      mutationFn: (alertId: string) => brokerageApi.dismissAlert(alertId),
    });
  },

  useActiveAlerts: (options?: Omit<UseQueryOptions<{ success: boolean; alerts: any[] }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'alerts', 'active'],
      queryFn: () => brokerageApi.getActiveAlerts(),
      staleTime: 10 * 1000, // Data stays fresh for 10 seconds
      ...options,
    });
  },

  useAlertStatus: (alertId: string | null, options?: Omit<UseQueryOptions<{ success: boolean; isDismissed: boolean }, Error>, 'queryKey' | 'queryFn'>) => {
    return useQuery({
      queryKey: ['brokerages', 'alerts', alertId, 'status'],
      queryFn: () => brokerageApi.getAlertStatus(alertId!),
      enabled: !!alertId,
      staleTime: 10 * 1000,
      ...options,
    });
  },
};

